<!--
    This component is intended to be used in place of the default vuejs-datepicker.

    We prefer our date input text field to have an inline calendar icon, and
    our datepicker to only appear when the calendar icon is clicked.

    Additionally, we prefer to bind to MM/dd/yyyy date strings rather than Date objects.

    This component manages the display state of the datepicker, and the
    translation back and forth between Date and String.

    It supports three optional attributes:
        * inputId allows you to set the id attribute of the wrapped text box.
            This allows you to hook up a label for the input element.
        * inputClass allows you to put a class on the wrapped text box.
            This allows you to dynamically add and remove classes.
        * disabledDates passes through to the vuejs-datepicker.
            This allows you to gray out certain dates.
            See their excellent documentation for more details.
        * openDate allows you to set the calendar to a certain date when it is opened.

    Example markup:

        <label for="dentist-appointment">When would you like your root canal?</label>
        <pulte-date-picker input-id="dentist-appointment"
                     :input-class="{'odd-date': dateIsOdd}"
                     :disabled-dates="{to: new Date()}"
                     v-model="appointmentDate" />
        <span class="placeholder"></span>

    Example parent component:
    {
        data() {
            return {
                appointmentDate: "",
            }
        },
        methods: {
            dateIsOdd() {
                const day = appointmentDate.split('/')[1].replace(/^0/, '');
                return parseInt(day) % 2;
            }
        }
    }

    Example parent style:
        .date-picker .odd-date {
            backgroundcolor: lightgreen;
        }

        .date-picker .odd-date + .placeholder::after {
            content: "Well, that's an odd day to see the dentist! \1f941";
        }
-->

<template>
    <span class="date-picker">
        <input v-bind:id="inputId"
               type="text"
               class="form-control lo-sensitive"
               v-bind:value="modelValue"
               v-mask="mask"
               v-bind:class="inputClass"
               :tabindex="tabindex"
               @input="emitInput($event.target.value)" />
        <datepicker class="vuejs-datepicker"
                    ref="calendar"
                    v-model="calendarValue"
                    :format="format"
                    :teleport="true"
                    inline auto-apply
                    :min-date="disabledDates?.to"
                    :max-date="disabledDates?.from"
                    prevent-min-max-navigation
                    v-show="calendarIsVisible"
                    @update:model-value="hideCalendar"
                    :start-date="openDate"
                    tabindex="-1"
                    :enable-time-picker="false"
                    :ui="{
                        menu: 'calender-style-override lo-sensitive'
                     }"
                    />
        <font-awesome-icon class="calendar-icon"
                           :icon="['far', 'calendar-alt']"
                           fixed-width
                           tabindex="-1"
                           @click.stop="toggleCalendarDisplay" />
    </span>
</template>

<script>
    import { vMaska } from "maska/vue";

    export default {
        name: 'pulte-date-picker',
        directives: { mask: vMaska },
        props: {
            modelValue: {
                type: String,
                required: true,
            },
            inputId: {
                type: String,
                required: false,
            },
            inputClass: {
                type: Object,
                required: false,
            },
            disabledDates: {
                type: Object,
                required: false,
            },
            openDate: {
                type: Date,
                required: false,
                default: () => new Date(),
            },
            tabindex: {
                type: String,
                default: '0'
            },
            format: {
                type: String,
                default: 'MM/dd/yyyy'
            }
        },
        data() {
            return {
                calendarIsVisible: false,
                calendarValue: null,
            };
        },
        computed: {
            parsedValue() {
                if (this.modelValue instanceof Date) {
                    // If the modelValue prop was bound to a Date, pass it through.
                    return this.modelValue;
                } else if (typeof this.modelValue === "string" && /\d{2}\/\d{2}\/\d{4}/.test(this.modelValue)) {
                    // If the modelValue prop was bound to a MM/dd/yyyy string, build a Date
                    const BASE_TEN = 10;
                    let [month, day, year] = this.modelValue.split("/").map(str => parseInt(str, BASE_TEN));
                    return new Date(year, month - 1, day);
                } else {
                    // Otherwise, who knows what the dev was thinking?
                    return null;
                }
            },
            minimumView() {
                if (this.format === 'MM/dd/yyyy') return 'day';
                else if (this.format === 'MM/yyyy') return 'month';
                return 'day';
            },
            mask() {
                if (this.format === 'MM/dd/yyyy') return '##/##/####';
                else if (this.format === 'MM/yyyy') return '##/####';
                return '##/##/####';
            }
        },
        created() {
            let touchEvent = 'ontouchstart' in window ? 'touchstart' : 'click';

            if (touchEvent !== 'touchstart') {
                // Temporary workaround: When opening the datepicker on some
                //    mobile devices, the datepicker appears offscreen.
                // The user has to scroll to see the datepicker, and in touching
                //    the screen to pan over, they will dismiss the datepicker.
                // Therefore, until we find a better solution, the only ways to
                //    dismiss the datepicker on a mobile device will be either
                //    to select a date, or tap the calendar icon a second time.
                window.addEventListener(touchEvent, this.handleGlobalClick);
            }
        },
        unmounted() {
            let touchEvent = 'ontouchstart' in window ? 'touchstart' : 'click';
            window.removeEventListener(touchEvent, this.handleGlobalClick);
        },
        methods: {
            handleGlobalClick(event) {
                if (this.shouldHideCalendarFor(event)) {
                    this.hideCalendar();
                }
            },
            hideCalendar() {
                this.calendarIsVisible = false;
            },
            toggleCalendarDisplay() {
                this.calendarIsVisible = !this.calendarIsVisible;
            },
            shouldHideCalendarFor(event) {
                return this.calendarIsVisible
                    && this.$refs
                    && this.$refs.calendar
                    && !this.$refs.calendar.$el.contains(event.target);
            },
            formatDate(dateValue) {
                // Create a MM/dd/yyyy or MM/yyyy formatted string from a Date object
                // based upon the format prop

                if (this.format == 'MM/dd/yyyy') {
                    let parts = [
                        dateValue.getMonth() + 1,
                        dateValue.getDate(),
                        dateValue.getFullYear()
                    ];
                    return parts
                        .map(part => part.toString().padStart(2, '0'))
                        .join('/');
                }
                else if (this.format == 'MM/yyyy') {
                    let parts = [
                        dateValue.getMonth() + 1,
                        dateValue.getFullYear()
                    ];
                    return parts
                        .map(part => part.toString().padStart(2, '0'))
                        .join('/');
                }

                return parts
                    .map(part => part.toString().padStart(2, '0'))
                    .join('/');
            },
            emitInput(value) {
                this.$emit('update:modelValue', value);
            }
        },
        watch: {
            calendarValue: {
                handler(value) {
                    this.emitInput(this.formatDate(value));
                }
            },
        },
    };
</script>

<style>
    .calender-style-override {
        width: 250px !important;
        font-size: 15px !important;
        position: absolute !important;
        margin-left: -5%;
    }

    @media screen and (max-width:768px) {
        #living-situation-previous .calender-style-override {
            margin-left: -52%;
        }
    }
</style>

<style scoped lang="scss">
    .date-picker {
        display: inline-block;
        position: relative;
        .vuejs-datepicker

    {
        display: block;
        font-size: 1rem;
        line-height: 1.5;
        color: #495057;
        background-color: #ffffff;
        background-clip: padding-box;
        border-radius: .25rem;
        transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
    }

    .calendar-icon {
        color: #004364;
        position: absolute;
        right: 10px;
        top: 11px;
        cursor: pointer;
    }

    @media screen and (max-width:768px) {
        .calendar-icon {
            right: 5px;
            top: 2px;
            width: 25px;
            height: 35px;
        }
    }
    }
</style>